// src/components/sections/Home/Hero.jsx

import {
  memo,
  useMemo,
} from "react";

import {
  FiArrowRight,
  FiCheckCircle,
  FiHeart,
  FiShield,
  FiSmile,
} from "react-icons/fi";

import {
  motion,
  useReducedMotion,
} from "framer-motion";

import Button from "../../UI/button";

import HeroImage from "../../../assets/FabianaTerno.jpeg";

import { footerTexts } from "../../utils/footerConstants";

import "../../../Styles/hero.css";

/* ==========================================================================
   CONFIGURAÇÕES
   ========================================================================== */

const HERO_FEATURES = [
  {
    id: "seguranca",
    label: "Procedimentos seguros",
    Icon: FiShield,
  },
  {
    id: "atendimento",
    label: "Atendimento humanizado",
    Icon: FiHeart,
  },
  {
    id: "resultados",
    label: "Resultados naturais",
    Icon: FiCheckCircle,
  },
];

const WHATSAPP_MESSAGE =
  "Olá! Vim pelo site da Clínica de Estética Catarino e gostaria de agendar uma avaliação.";

const EASE_PREMIUM = [
  0.16,
  1,
  0.3,
  1,
];

/* ==========================================================================
   VARIANTES DE ANIMAÇÃO
   ========================================================================== */

const sectionVariants = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.12,
    },
  },
};

const contentVariants = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.12,
    },
  },
};

const contentItemVariants = {
  hidden: {
    opacity: 0,
    y: 34,
    filter: "blur(6px)",
  },

  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",

    transition: {
      duration: 0.75,
      ease: EASE_PREMIUM,
    },
  },
};

const visualVariants = {
  hidden: {
    opacity: 0,
    x: 60,
    scale: 0.95,
    filter: "blur(9px)",
  },

  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    filter: "blur(0px)",

    transition: {
      duration: 1.05,
      delay: 0.18,
      ease: EASE_PREMIUM,
    },
  },
};

const featureContainerVariants = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      staggerChildren: 0.1,
    },
  },
};

const featureVariants = {
  hidden: {
    opacity: 0,
    y: 22,
    scale: 0.96,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      duration: 0.58,
      ease: EASE_PREMIUM,
    },
  },
};

const floatingCardVariants = {
  hidden: {
    opacity: 0,
    y: 24,
    scale: 0.88,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      duration: 0.65,
      delay: 0.75,
      ease: EASE_PREMIUM,
    },
  },
};

const qualityBadgeVariants = {
  hidden: {
    opacity: 0,
    x: -22,
    scale: 0.9,
  },

  visible: {
    opacity: 1,
    x: 0,
    scale: 1,

    transition: {
      duration: 0.6,
      delay: 0.65,
      ease: EASE_PREMIUM,
    },
  },
};

/* ==========================================================================
   UTILITÁRIOS
   ========================================================================== */

function normalizePhone(phone = "") {
  return String(phone).replace(
    /\D/g,
    "",
  );
}

/* ==========================================================================
   COMPONENTE
   ========================================================================== */

function Hero({
  phone = footerTexts?.phone ??
    "5511921881464",
}) {
  const prefersReducedMotion =
    useReducedMotion();

  const whatsappUrl = useMemo(() => {
    const normalizedPhone =
      normalizePhone(phone);

    if (!normalizedPhone) {
      return "#";
    }

    return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(
      WHATSAPP_MESSAGE,
    )}`;
  }, [phone]);

  const disableMotion =
    prefersReducedMotion === true;

  return (
    <motion.section
      className="hero"
      id="home"
      aria-labelledby="hero-title"
      variants={
        disableMotion
          ? undefined
          : sectionVariants
      }
      initial={
        disableMotion
          ? false
          : "hidden"
      }
      animate={
        disableMotion
          ? undefined
          : "visible"
      }
    >
      {/* =========================================================
          FUNDO DECORATIVO
      ========================================================== */}

      <div
        className="hero__background"
        aria-hidden="true"
      >
        <motion.span
          className="hero__orb hero__orb--left"
          animate={
            disableMotion
              ? undefined
              : {
                  x: [0, 16, 0],
                  y: [0, -18, 0],
                  scale: [
                    1,
                    1.04,
                    1,
                  ],
                }
          }
          transition={{
            duration: 14,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
          }}
        />

        <motion.span
          className="hero__orb hero__orb--right"
          animate={
            disableMotion
              ? undefined
              : {
                  x: [0, -14, 0],
                  y: [0, 20, 0],
                  scale: [
                    1,
                    1.035,
                    1,
                  ],
                }
          }
          transition={{
            duration: 17,
            delay: 1,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
          }}
        />

        <motion.span
          className="hero__pattern"
          initial={
            disableMotion
              ? false
              : {
                  opacity: 0,
                }
          }
          animate={
            disableMotion
              ? undefined
              : {
                  opacity: 0.16,
                }
          }
          transition={{
            duration: 1.4,
            delay: 0.35,
          }}
        />
      </div>

      <div className="hero__container">
        {/* =====================================================
            CONTEÚDO
        ====================================================== */}

        <motion.div
          className="hero__content"
          variants={
            disableMotion
              ? undefined
              : contentVariants
          }
        >
          <motion.span
            className="hero__eyebrow"
            variants={
              disableMotion
                ? undefined
                : contentItemVariants
            }
            whileHover={
              disableMotion
                ? undefined
                : {
                    y: -2,
                    scale: 1.015,
                  }
            }
            transition={{
              duration: 0.35,
              ease: EASE_PREMIUM,
            }}
          >
            <motion.span
              className="hero__eyebrow-line"
              aria-hidden="true"
              initial={
                disableMotion
                  ? false
                  : {
                      scaleX: 0,
                    }
              }
              animate={
                disableMotion
                  ? undefined
                  : {
                      scaleX: 1,
                    }
              }
              transition={{
                duration: 0.75,
                delay: 0.55,
                ease: EASE_PREMIUM,
              }}
              style={{
                transformOrigin: "left",
              }}
            />

            Clínica de estética avançada
          </motion.span>

          <motion.h1
            className="hero__title"
            id="hero-title"
            variants={
              disableMotion
                ? undefined
                : contentItemVariants
            }
          >
            Realce sua beleza com{" "}

            <motion.span
              className="hero__highlight"
              initial={
                disableMotion
                  ? false
                  : {
                      opacity: 0,
                      y: 14,
                    }
              }
              animate={
                disableMotion
                  ? undefined
                  : {
                      opacity: 1,
                      y: 0,
                    }
              }
              transition={{
                duration: 0.75,
                delay: 0.5,
                ease: EASE_PREMIUM,
              }}
            >
              elegância e naturalidade
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero__description"
            variants={
              disableMotion
                ? undefined
                : contentItemVariants
            }
          >
            Tratamentos personalizados,
            tecnologia avançada e
            atendimento humanizado para
            valorizar sua beleza natural e
            elevar sua autoestima.
          </motion.p>

          {/* =================================================
              AÇÕES
          ================================================== */}

          <motion.div
            className="hero__actions"
            variants={
              disableMotion
                ? undefined
                : contentItemVariants
            }
          >
            <motion.div
              whileHover={
                disableMotion
                  ? undefined
                  : {
                      y: -4,
                      scale: 1.018,
                    }
              }
              whileTap={{
                scale: 0.97,
              }}
              transition={{
                duration: 0.35,
                ease: EASE_PREMIUM,
              }}
            >
              <Button
                variant="primary"
                className="hero__button hero__button--primary"
                href="/servicos"
                aria-label="Conhecer os tratamentos disponíveis"
              >
                <span>
                  Conheça os tratamentos
                </span>

                <FiArrowRight
                  aria-hidden="true"
                />
              </Button>
            </motion.div>

            <motion.div
              whileHover={
                disableMotion
                  ? undefined
                  : {
                      y: -4,
                      scale: 1.018,
                    }
              }
              whileTap={{
                scale: 0.97,
              }}
              transition={{
                duration: 0.35,
                ease: EASE_PREMIUM,
              }}
            >
              <Button
                variant="outline"
                className="hero__button hero__button--outline"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Agendar avaliação pelo WhatsApp"
              >
                Agendar avaliação
              </Button>
            </motion.div>
          </motion.div>

          {/* =================================================
              DIFERENCIAIS
          ================================================== */}

          <motion.ul
            className="hero__features"
            aria-label="Diferenciais da clínica"
            variants={
              disableMotion
                ? undefined
                : featureContainerVariants
            }
          >
            {HERO_FEATURES.map(
              ({
                id,
                label,
                Icon,
              }) => (
                <motion.li
                  key={id}
                  className="hero__feature"
                  variants={
                    disableMotion
                      ? undefined
                      : featureVariants
                  }
                  whileHover={
                    disableMotion
                      ? undefined
                      : {
                          y: -5,
                          scale: 1.018,
                        }
                  }
                  whileTap={{
                    scale: 0.985,
                  }}
                  transition={{
                    duration: 0.35,
                    ease:
                      EASE_PREMIUM,
                  }}
                >
                  <motion.span
                    className="hero__feature-icon"
                    aria-hidden="true"
                    whileHover={
                      disableMotion
                        ? undefined
                        : {
                            rotate: -7,
                            scale:
                              1.1,
                          }
                    }
                    transition={{
                      duration: 0.35,
                      ease:
                        EASE_PREMIUM,
                    }}
                  >
                    <Icon />
                  </motion.span>

                  <span className="hero__feature-label">
                    {label}
                  </span>
                </motion.li>
              ),
            )}
          </motion.ul>

          {/* =================================================
              PROVA SOCIAL
          ================================================== */}

          <motion.div
            className="hero__social-proof"
            variants={
              disableMotion
                ? undefined
                : contentItemVariants
            }
          >
            

          </motion.div>
        </motion.div>

        {/* =====================================================
            VISUAL
        ====================================================== */}

        <motion.div
          className="hero__visual"
          variants={
            disableMotion
              ? undefined
              : visualVariants
          }
        >
          <div className="hero__image-wrapper">
            <motion.span
              className="hero__image-decoration"
              aria-hidden="true"
              initial={
                disableMotion
                  ? false
                  : {
                      opacity: 0,
                      rotate: 0,
                      scale: 0.92,
                    }
              }
              animate={
                disableMotion
                  ? undefined
                  : {
                      opacity: 1,
                      rotate: 4,
                      scale: 1,
                    }
              }
              transition={{
                duration: 1,
                delay: 0.45,
                ease: EASE_PREMIUM,
              }}
            />

            <motion.figure
              className="hero__image-frame"
              whileHover={
                disableMotion
                  ? undefined
                  : {
                      y: -8,
                      scale: 1.008,
                    }
              }
              transition={{
                duration: 0.65,
                ease: EASE_PREMIUM,
              }}
            >
              <motion.img
                src={HeroImage}
                alt="Dra. Fabiana, profissional da Clínica de Estética Catarino"
                className="hero__image"
                decoding="async"
                fetchPriority="high"
                initial={
                  disableMotion
                    ? false
                    : {
                        scale: 1.08,
                      }
                }
                animate={
                  disableMotion
                    ? undefined
                    : {
                        scale: 1.01,
                      }
                }
                whileHover={
                  disableMotion
                    ? undefined
                    : {
                        scale: 1.05,
                      }
                }
                transition={{
                  duration: 1.1,
                  ease: EASE_PREMIUM,
                }}
              />

              <span
                className="hero__image-overlay"
                aria-hidden="true"
              />
            </motion.figure>

            {/* Card flutuante */}

            

            {/* Selo de qualidade */}

            <motion.div
              className="hero__quality-badge"
              variants={
                disableMotion
                  ? undefined
                  : qualityBadgeVariants
              }
              animate={
                disableMotion
                  ? undefined
                  : {
                      y: [
                        0,
                        5,
                        0,
                      ],
                    }
              }
              whileHover={
                disableMotion
                  ? undefined
                  : {
                      scale: 1.04,
                      x: -3,
                    }
              }
              transition={{
                y: {
                  duration: 4.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                },

                scale: {
                  duration: 0.35,
                  ease: EASE_PREMIUM,
                },
              }}
            >
              <motion.span
                aria-hidden="true"
                animate={
                  disableMotion
                    ? undefined
                    : {
                        scale: [
                          1,
                          1.15,
                          1,
                        ],
                      }
                }
                transition={{
                  duration: 2.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <FiHeart />
              </motion.span>

              <span>
                Cuidado em cada detalhe
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* =========================================================
          INDICADOR DE ROLAGEM
      ========================================================== */}

      <motion.a
        href="/Servicos"
        className="hero__scroll-indicator"
        aria-label="Rolar para a seção sobre"
        initial={
          disableMotion
            ? false
            : {
                opacity: 0,
                y: 16,
              }
        }
        animate={
          disableMotion
            ? undefined
            : {
                opacity: 1,
                y: 0,
              }
        }
        transition={{
          duration: 0.7,
          delay: 1.35,
          ease: EASE_PREMIUM,
        }}
        whileHover={
          disableMotion
            ? undefined
            : {
                y: -4,
              }
        }
      >
        <span>
          Descubra mais
        </span>

        <motion.span
          className="hero__scroll-line"
          aria-hidden="true"
          animate={
            disableMotion
              ? undefined
              : {
                  scaleY: [
                    0.55,
                    1,
                    0.55,
                  ],
                  opacity: [
                    0.35,
                    1,
                    0.35,
                  ],
                }
          }
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            transformOrigin: "top",
          }}
        />
      </motion.a>
    </motion.section>
  );
}

export default memo(Hero);